import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { streamChat } from "@/lib/stream";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, Loader2, ChevronRight } from "lucide-react";
import MealTracker from "@/components/MealTracker";

export default function Nutrition() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [result, setResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [form, setForm] = useState({
    age: user?.age || 30, sex: user?.sex || "male", height_in: user?.height_in || 69,
    weight_lb: user?.weight_lb || 165, activity_level: "moderate", goal: user?.goal || "fat_loss", rate: "moderate",
    use_body_fat: false, body_fat_pct: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // AI chat
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/nutrition/articles").then(({ data }) => setArticles(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/ai/history/nutrition").then(({ data }) => setMessages(data.map((m) => ({ role: m.role, text: m.text })))).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const calculate = async () => {
    setCalcLoading(true);
    try {
      const bf = parseFloat(form.body_fat_pct);
      const payload = {
        age: parseInt(form.age), sex: form.sex, height_in: parseFloat(form.height_in),
        weight_lb: parseFloat(form.weight_lb), activity_level: form.activity_level, goal: form.goal, rate: form.rate,
      };
      if (form.use_body_fat && !isNaN(bf) && bf > 0 && bf < 60) payload.body_fat_pct = bf;
      const { data } = await api.post("/nutrition/calculate", payload);
      setResult(data);
    } finally { setCalcLoading(false); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      await streamChat("/ai/nutrition/stream", msg, (chunk) => {
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + chunk }; return c; });
      });
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "Error, try again." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <AppShell title="Nutrition">
      <Tabs defaultValue="calculator">
        <TabsList className="bg-card border border-border w-full">
          <TabsTrigger value="calculator" data-testid="tab-calculator" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Calc</TabsTrigger>
          <TabsTrigger value="tracker" data-testid="tab-tracker" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Tracker</TabsTrigger>
          <TabsTrigger value="learn" data-testid="tab-learn" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Learn</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Ask AI</TabsTrigger>
        </TabsList>

        <TabsContent value="tracker">
          <MealTracker />
        </TabsContent>

        {/* Calculator */}
        <TabsContent value="calculator" className="space-y-4 mt-4">
          <Card className="rounded-2xl bg-card border-border p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Age</Label><Input data-testid="calc-age" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} className="bg-card border-border h-10" /></div>
              <div><Label className="text-xs">Sex</Label>
                <Select value={form.sex} onValueChange={(v) => set("sex", v)}><SelectTrigger className="bg-card border-border h-10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs">Height (in)</Label><Input data-testid="calc-height" type="number" value={form.height_in} onChange={(e) => set("height_in", e.target.value)} className="bg-card border-border h-10" /></div>
              <div><Label className="text-xs">Weight (lb)</Label><Input data-testid="calc-weight" type="number" value={form.weight_lb} onChange={(e) => set("weight_lb", e.target.value)} className="bg-card border-border h-10" /></div>
            </div>
            <div><Label className="text-xs">Activity level</Label>
              <Select value={form.activity_level} onValueChange={(v) => set("activity_level", v)}><SelectTrigger data-testid="calc-activity" className="bg-card border-border h-10"><SelectValue /></SelectTrigger><SelectContent>
                <SelectItem value="sedentary">Sedentary</SelectItem><SelectItem value="light">Light</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="very_active">Very active</SelectItem>
              </SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Goal</Label>
                <Select value={form.goal} onValueChange={(v) => set("goal", v)}><SelectTrigger data-testid="calc-goal" className="bg-card border-border h-10"><SelectValue /></SelectTrigger><SelectContent>
                  <SelectItem value="fat_loss">Fat loss</SelectItem><SelectItem value="maintain">Maintain</SelectItem><SelectItem value="muscle_gain">Muscle gain</SelectItem>
                </SelectContent></Select>
              </div>
              <div><Label className="text-xs">Rate</Label>
                <Select value={form.rate} onValueChange={(v) => set("rate", v)}><SelectTrigger className="bg-card border-border h-10"><SelectValue /></SelectTrigger><SelectContent>
                  <SelectItem value="slow">Slow</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="aggressive">Aggressive</SelectItem>
                </SelectContent></Select>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/60 p-3 space-y-3">
              <label htmlFor="use-body-fat" className="flex items-start gap-3 cursor-pointer">
                <Checkbox id="use-body-fat" data-testid="calc-use-bodyfat" checked={form.use_body_fat} onCheckedChange={(v) => set("use_body_fat", !!v)} className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium">Use body fat % for higher accuracy</span>
                  <span className="block text-[11px] text-muted-foreground">Switches to the Katch‑McArdle formula (based on lean body mass)</span>
                </span>
              </label>
              {form.use_body_fat && (
                <div>
                  <Label className="text-xs">Body fat %</Label>
                  <Input data-testid="calc-bodyfat" type="number" min="1" max="59" step="0.1" placeholder="e.g. 18" value={form.body_fat_pct} onChange={(e) => set("body_fat_pct", e.target.value)} className="bg-card border-border h-10" />
                  <p className="text-[11px] text-muted-foreground mt-1">Tip: many smart scales estimate this. Leave the box unchecked to use the standard formula.</p>
                </div>
              )}
            </div>
            <Button data-testid="calc-submit" onClick={calculate} disabled={calcLoading} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">{calcLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Calculate targets"}</Button>
          </Card>

          {result && (
            <Card data-testid="calc-results" className="rounded-2xl bg-card border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Your daily targets</p>
                <span data-testid="calc-formula" className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {result.formula === "katch_mcardle" ? "Katch‑McArdle" : "Mifflin‑St Jeor"}
                </span>
              </div>
              {result.formula === "katch_mcardle" && result.lean_mass_lb != null && (
                <p className="text-[11px] text-muted-foreground -mt-2 mb-3">
                  Based on <span className="rd-lime font-semibold">{result.lean_mass_lb} lb</span> lean mass ({result.body_fat_pct}% body fat) &middot; protein set from lean mass.
                </p>
              )}
              <div className="grid grid-cols-4 gap-2 text-center mb-4">
                {[["Cals", result.target_calories],["Protein", result.protein_g + "g"],["Carbs", result.carb_g + "g"],["Fat", result.fat_g + "g"]].map(([l, v]) => (
                  <div key={l}><p className="font-mono text-xl font-bold rd-lime">{v}</p><p className="text-[11px] text-muted-foreground">{l}</p></div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                {[["Maintenance", result.maintenance_calories],["Fat loss", result.fat_loss_calories],["Muscle gain", result.muscle_gain_calories]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-muted py-2"><p className="font-mono font-semibold">{v}</p><p className="text-muted-foreground">{l}</p></div>
                ))}
              </div>
              <Button onClick={() => send(`Build me a 1-day sample meal plan hitting ${result.target_calories} calories and ${result.protein_g}g protein.`)} className="w-full mt-4 bg-secondary border border-border text-foreground hover:border-[#ccff0c]/40">
                Get a meal plan from AI
              </Button>
            </Card>
          )}
        </TabsContent>

        {/* Learn */}
        <TabsContent value="learn" className="space-y-2 mt-4">
          {articles.map((a) => (
            <Card key={a.id} data-testid={`article-${a.id}`} onClick={() => navigate(`/nutrition/${a.id}`)} className="rounded-xl bg-card border-border p-4 cursor-pointer hover:border-[#ccff0c]/40 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.section} · {a.read_min} min read</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai" className="mt-4">
          <div className="flex flex-col" style={{ height: "calc(100vh - 240px)" }}>
            <div data-testid="nutrition-ai-list" className="flex-1 overflow-y-auto space-y-3 pb-4">
              {messages.length === 0 && <p className="text-muted-foreground text-center py-8">Ask about meals, macros, or estimate calories from a meal.</p>}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#ccff0c] text-black rounded-2xl rounded-br-md" : "bg-secondary text-foreground border border-border rounded-2xl rounded-bl-md"}`}>
                    {m.text || (busy && i === messages.length - 1 ? <span className="rd-typing"><span></span><span></span><span></span></span> : "")}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="flex items-end gap-2 pt-2 border-t border-border">
              <Textarea data-testid="nutrition-ai-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask about nutrition…" className="bg-card border-border resize-none min-h-[44px]" rows={1} />
              <Button data-testid="nutrition-ai-send" onClick={() => send()} disabled={busy || !input.trim()} className="h-11 w-11 p-0 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] flex-shrink-0"><Send className="h-5 w-5" /></Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
